import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { useI18n } from '@/features/i18n/I18nProvider';
import { supabase } from '@/lib/supabase';
import {
  computeAccessState,
  hasFeatureAccess,
  isAllowedWhenLocked,
  type ActiveSubscription,
  type AccessState,
} from '@/features/subscription/access';

export function useAccessState() {
  const { profile, session, loading: authLoading, profileLoading } = useAuth();
  const [subscription, setSubscription] = useState<ActiveSubscription | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (authLoading || profileLoading) {
      setLoaded(false);
      return;
    }

    if (!session) {
      setSubscription(null);
      setLoaded(true);
      return;
    }

    // Session présente mais profil pas encore disponible : ne pas traiter comme « locked »
    if (!profile) {
      setLoaded(false);
      return;
    }

    let cancelled = false;
    setLoaded(false);
    void supabase
      .from('subscriptions')
      .select('plan_type, status, ends_on, installments_paid, installment_count, next_installment_at')
      .eq('user_id', profile.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setSubscription((data as ActiveSubscription | null) ?? null);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [profile, session, authLoading, profileLoading]);

  const state: AccessState = computeAccessState(profile, subscription);
  return { state, loaded, subscription, hasAccess: hasFeatureAccess(state) };
}

export function RequireFeatureAccess({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const { t } = useI18n();
  const { state, loaded, hasAccess } = useAccessState();

  // Pas de page intermédiaire plein écran : on garde le shell et on attend silencieusement.
  if (!loaded) {
    return <div className="shell-boot" aria-busy="true" aria-label="Chargement" />;
  }

  if (hasAccess || isAllowedWhenLocked(location.pathname)) {
    return children ?? null;
  }

  if (location.pathname !== '/app/abonnement') {
    return <Navigate to="/app/abonnement" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="u-page">
      <section className="u-card">
        <h2>{t('access.lockedTitle')}</h2>
        <p>{t('access.lockedBody')}</p>
        <Link className="btn btn-primary" to="/app/abonnement">
          {t('access.goSubscribe')}
        </Link>
        <p className="muted" style={{ marginTop: '0.75rem' }}>
          {state.status === 'locked' && state.reason === 'subscription_ended'
            ? t('sub.subscriptionEndedBanner')
            : state.status === 'locked'
              ? t('sub.lockedBanner')
              : null}
        </p>
      </section>
    </div>
  );
}
